import { useEffect, useId, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { useSnackbar } from "notistack";
import { coin } from "src/assets";
import { send_coins } from "src/DAL/Community/Community";
import { useContentSetting } from "src/Hooks/ContentContext/ContentSettingState";
import { memberDetail } from "src/DAL/Profile/Profile";
import { gr, tokens } from "src/design-system/tokens";
import GrButton from "src/design-system/components/GrButton";
import GrText from "src/design-system/components/GrText";
import GrTextField from "src/design-system/components/GrTextField";
import GrDialog from "src/design-system/components/GrDialog";
import GrPanel from "src/design-system/components/GrPanel";

// "Send coins to {name}": the available points (prominent, on top), a comment, the coin count
// (never more than the member's available coins) and Submit. The available points count down while
// the member types the coin count.
const formSx = { display: "grid", gap: gr.space[4], mt: gr.space[2] };
const availableSx = { display: "flex", alignItems: "center", gap: gr.space[3] };
const coinImageSx = { width: gr.space[10], height: gr.space[10], flex: "none" };
const availableNumberSx = { color: gr.color.brandStrong };
const formatCoins = (value) => (Number(value) || 0).toLocaleString("en-IE");

export default function SendCoins({
  sendCoins,
  handleCloseSimpleBox,
  feeds_list,
  handleUpdateSpecificFeed,
}) {
  const formId = `send-coins-${useId()}`;
  const [comment, setComment] = useState("");
  const [coinCount, setCoinCount] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const { userInfo, setUserInfo } = useContentSetting();
  const [totalPoints, setTotalPoints] = useState(0);
  const [availablecoins, setAvailablecoins] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  // True while the member tries to type more than the available coins (the field keeps the last
  // allowed value); only colours the helper line red.
  const [overLimit, setOverLimit] = useState(false);
  const canSend =
    coinCount !== "" && coinCount > 0 && coinCount <= availablecoins;
  const handleCoinCountChange = (e) => {
    const { value } = e.target;
    if (value === "") {
      setCoinCount("");
      setOverLimit(false);
      return;
    }
    const input = parseInt(value, 10);
    if (isNaN(input) || input < 0) return;
    if (input > availablecoins) {
      setOverLimit(true);
      return;
    }
    setCoinCount(input);
    setOverLimit(false);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    let postData = {
      shared_points: coinCount,
      description: comment,
      receiver: feeds_list.created_by._id,
      feed: feeds_list._id,
    };
    if (coinCount <= 0) {
      enqueueSnackbar("Please Add Coins to Send", { variant: "error" });
    } else {
      setIsLoading(true);
      const result = await send_coins(postData);
      if (result.code === 200) {
        // A new object (not the same one changed in place), so screens reading userInfo re-render.
        setUserInfo({ ...userInfo, total_points: totalPoints });
        enqueueSnackbar(result.message, { variant: "success" });
        handleUpdateSpecificFeed(feeds_list._id);
        handleCloseSimpleBox();
        setIsLoading(false);
      } else {
        enqueueSnackbar(result.message, { variant: "error" });
        setIsLoading(false);
      }
    }
  };

  const getMemberData = async () => {
    const result = await memberDetail();
    if (result.code === 200) {
      setAvailablecoins(result?.member?.total_points);
      setTotalPoints(result?.member?.total_points);
    }
  };

  useEffect(() => {
    let coins = availablecoins - coinCount;
    if (coins >= 0) {
      setTotalPoints(coins);
    } else {
      setTotalPoints(availablecoins);
    }
  }, [coinCount]);
  useEffect(() => {
    getMemberData();
  }, []);

  return (
    <GrDialog
      showClose
      open={Boolean(sendCoins)}
      onClose={handleCloseSimpleBox}
      title={`Send coins to ${feeds_list.created_by?.name}`}
      actions={
        <GrButton
          type="submit"
          form={formId}
          loading={isLoading}
          disabled={!canSend}
        >
          Submit
        </GrButton>
      }
    >
      <Box component="form" id={formId} onSubmit={handleSubmit} sx={formSx}>
        <GrPanel sx={availableSx}>
          <Box
            component="img"
            src={coin}
            alt=""
            width={tokens.space[10]}
            height={tokens.space[10]}
            sx={coinImageSx}
          />
          <div>
            <GrText variant="secondary">Available points are</GrText>
            <GrText variant="number" sx={availableNumberSx}>
              {formatCoins(totalPoints)}
            </GrText>
          </div>
        </GrPanel>
        <GrTextField
          label="Add a comment"
          multiline
          minRows={2}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <GrTextField
          label="Coin count"
          required
          type="number"
          value={coinCount}
          onChange={handleCoinCountChange}
          onWheel={(event) => event.target.blur()}
          // The browser's spinner and keyboard arrows stop at 0 and at the available coins.
          inputProps={{
            min: 0,
            max: availablecoins,
            step: 1,
            inputMode: "numeric",
          }}
          error={overLimit}
          helperText="(You cannot send coins more than your available coins.)"
        />
      </Box>
    </GrDialog>
  );
}

SendCoins.propTypes = {
  sendCoins: PropTypes.bool,
  handleCloseSimpleBox: PropTypes.func.isRequired,
  feeds_list: PropTypes.object.isRequired,
  handleUpdateSpecificFeed: PropTypes.func.isRequired,
};
